import { action, computed, observable } from 'mobx'
import {
  BusinessType,
  type MethodUsedToValueClosingInventory,
} from '@taxfyle/txf-api-client'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { validationContext, required } from 'validx'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  mustBeValidEin,
  validateObject,
  when,
} from '@/utils/validx-util'
import { AddressFieldSetViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/AddressFieldSet'
import {
  mapOwnerToTaxActivityAssignee,
  type Owner,
} from '../../../TaxPrepOrganizerViewModel'
import {
  type BusinessActivity,
  type BusinessActivityProps,
  type Form1099PaymentsInfo,
  type InventoryInfo,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/models'
import { BUSINESS_TYPES_MAP } from '../../../data/business-types'

export class BusinessAccordionViewModel implements ActivationLifecycle {
  readonly lifecycle = new LifecycleTracker()

  @observable accessor isExpanded = false
  @observable accessor businessType: BusinessType = BusinessType.LLC

  @observable accessor businessName = ''
  @observable accessor ein = ''
  @observable accessor ownerType: Owner['type'] | null = null
  @observable accessor businessCode = ''
  @observable accessor principleBusiness = ''
  @observable accessor didMateriallyParticipateDuringYear: boolean | null = null
  @observable accessor businessWasStartedOrAcquiredDuringYear: boolean | null =
    null
  @observable accessor businessWasDisposedOfDuringYear: boolean | null = null
  @observable accessor carryToForm8960: boolean | null = null
  @observable accessor professionalGambler: boolean | null = null
  @observable accessor exemptNotaryIncome: boolean | null = null
  @observable accessor paperRouteExcludedFromSe: boolean | null = null
  @observable accessor didMakePaymentsRequiring1099: boolean | null = null
  @observable accessor willFileForm1099: boolean | null = null
  @observable accessor hasInventoryFlag: boolean | null = null
  @observable
  accessor inventoryMethod: MethodUsedToValueClosingInventory | null = null

  readonly address = new AddressFieldSetViewModel(false)

  readonly validation = validationContext(this, {
    ownerType: [required('Owner is required')],
    businessType: [required('Business type is required')],
    businessName: [
      when(() => this.isLLC, required('Business name is required')),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(90, 'Business name must not exceed 90 characters'),
    ],
    ein: [
      when(() => this.isLLC, required('EIN is required')),
      mustBeValidEin(),
    ],
    principleBusiness: [
      when(() => this.isSoleProp, required('Business activity is required')),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(
        90,
        'Business activity description must not exceed 90 characters',
      ),
    ],
    address: [validateObject()],
    willFileForm1099: [
      when(
        () => this.didMakePaymentsRequiring1099 === true,
        required(
          'Will you file the required 1099 forms for those payments? is required',
        ),
      ),
    ],
    inventoryMethod: [
      when(
        () => this.hasInventoryFlag === true,
        required('Inventory method is required'),
      ),
    ],
  })

  constructor(
    private readonly _model: BusinessActivity,
    private readonly _index: number,
    private readonly _year: number,
    private readonly _getOwners: () => Owner[],
  ) {
    this.syncFromModel()
  }

  @computed
  get index() {
    return this._index
  }

  @computed
  get id() {
    return this._model.id
  }

  @computed
  get year() {
    return this._year
  }

  @computed
  get isArchived() {
    return this._model.archived
  }

  @computed
  get displayName() {
    const name = this.isLLC ? this.businessName : this.principleBusiness
    return name || this.address.streetLine || `Business ${this._index + 1}`
  }

  @computed
  get displaySubtitle() {
    return this.businessType ? BUSINESS_TYPES_MAP[this.businessType] : null
  }

  @computed
  get owners() {
    return this._getOwners()
  }

  @computed
  get owner() {
    return (
      this.owners.find((owner) => owner.type === this.ownerType) ?? {
        type: 'UNASSIGNED',
        label: 'Unassigned',
      }
    )
  }

  @computed
  get isLLC() {
    return this.businessType === BusinessType.LLC
  }

  @computed
  get isSoleProp() {
    return this.businessType === BusinessType.SoleProp
  }

  toProps(): BusinessActivityProps {
    const form1099Payments: Form1099PaymentsInfo | null =
      this.didMakePaymentsRequiring1099 !== null
        ? this.didMakePaymentsRequiring1099
          ? {
              didMakePayments: true,
              willFileForm1099: this.willFileForm1099 ?? false,
            }
          : { didMakePayments: false }
        : null

    const inventory: InventoryInfo | null =
      this.hasInventoryFlag !== null
        ? this.hasInventoryFlag
          ? {
              hasInventory: true,
              methodUsedToValueClosingInventory: this.inventoryMethod,
            }
          : { hasInventory: false }
        : null

    return {
      id: this._model.id,
      businessType: this.businessType,
      businessName: this.businessName || null,
      ein: this.ein || null,
      assignedTo: mapOwnerToTaxActivityAssignee(this.owner),
      businessCode: this.businessCode ? parseInt(this.businessCode, 10) : null,
      principleBusiness: this.principleBusiness || null,
      address: this.address.toProps(),
      didNotMateriallyParticipateDuringYear: this
        .didMateriallyParticipateDuringYear
        ? !this.didMateriallyParticipateDuringYear
        : null,
      businessWasStartedOrAcquiredDuringYear:
        this.businessWasStartedOrAcquiredDuringYear,
      businessWasDisposedOfDuringYear: this.businessWasDisposedOfDuringYear,
      carryToForm8960: this.carryToForm8960,
      professionalGambler: this.professionalGambler,
      exemptNotaryIncome: this.exemptNotaryIncome,
      paperRouteExcludedFromSe: this.paperRouteExcludedFromSe,
      form1099Payments,
      inventory,
      archived: this._model.archived,
    }
  }

  @action.bound
  syncFromModel() {
    const model = this._model
    this.ownerType = model.assignedTo?.type ?? null
    this.businessName = model.businessName ?? ''
    this.ein = model.ein ?? ''
    this.businessCode = model.businessCode?.toString() ?? ''
    this.principleBusiness = model.principleBusiness ?? ''
    this.businessType = model.businessType
    this.address.reset(model.address)
    this.didMateriallyParticipateDuringYear =
      model.didNotMateriallyParticipateDuringYear !== null
        ? !model.didNotMateriallyParticipateDuringYear
        : null
    this.businessWasStartedOrAcquiredDuringYear =
      model.businessWasStartedOrAcquiredDuringYear
    this.businessWasDisposedOfDuringYear = model.businessWasDisposedOfDuringYear
    this.carryToForm8960 = model.carryToForm8960
    this.professionalGambler = model.professionalGambler
    this.exemptNotaryIncome = model.exemptNotaryIncome
    this.paperRouteExcludedFromSe = model.paperRouteExcludedFromSe

    if (model.form1099Payments) {
      this.didMakePaymentsRequiring1099 = model.form1099Payments.didMakePayments
      this.willFileForm1099 = model.form1099Payments.didMakePayments
        ? model.form1099Payments.willFileForm1099
        : null
    } else {
      this.didMakePaymentsRequiring1099 = null
      this.willFileForm1099 = null
    }

    if (model.inventory) {
      this.hasInventoryFlag = model.inventory.hasInventory
      this.inventoryMethod = model.inventory.hasInventory
        ? model.inventory.methodUsedToValueClosingInventory
        : null
    } else {
      this.hasInventoryFlag = null
      this.inventoryMethod = null
    }

    this.validation.reset()
  }

  @action.bound
  toggleExpanded() {
    this.isExpanded = !this.isExpanded
  }

  @action.bound
  expand() {
    this.isExpanded = true
  }

  activate() {
    this.lifecycle.activate()
  }

  deactivate() {
    this.lifecycle.deactivate()
  }
}
